import { CommonModule } from "@angular/common";
import { HttpContext } from "@angular/common/http";
import { Component, ElementRef, HostListener, OnDestroy, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { v4 as uuidv4 } from "uuid";
import { CompanyApiService } from "../../services/generated/waba-api/services/company-api.service";
import { StoreStatus } from "../../services/generated/waba-api/models/store-status";
import { StoreStatusUpdate } from "../../services/generated/waba-api/models/store-status-update";
import { SKIP_ERROR_ALERT } from "../interceptors/skip-error-alert.context";
import { getCornerDrawerDialogPosition } from "../utils/rtl-layout.util";
import { WorkHourPopupComponent } from "../../components/standalones/work-hour-popup/work-hour-popup.component";

/**
 * Header "store open / closed" switch: the merchant closes the in-app store for a while (30 min, 1 h, 2 h,
 * until the next opening of its work hours, or until reopened by hand) and chooses whether the work hours
 * open and close it. Closed, the in-app shows a red banner and takes no order (waba store-status).
 * Hidden when the user cannot read the store status.
 */
@Component({
  selector: "app-store-status-button",
  standalone: true,
  imports: [CommonModule, MatTooltipModule, TranslateModule],
  templateUrl: "./store-status-button.component.html",
  styleUrl: "./store-status-button.component.scss",
})
export class StoreStatusButtonComponent implements OnInit, OnDestroy {
  readonly durations = [30, 60, 120];
  status: StoreStatus | null = null;
  open = false;
  saving = false;
  error = false;
  /** The work-hours drawer is opening (the company's numbers are being read). */
  openingHours = false;
  /** The company has no WhatsApp number yet, so there is nowhere to set hours. */
  noNumber = false;

  private readonly silent = new HttpContext().set(SKIP_ERROR_ALERT, true);
  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    private companyApiService: CompanyApiService,
    private translateService: TranslateService,
    private hostRef: ElementRef<HTMLElement>,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.load();
    // A closure that ends, or the work hours, change the state without the merchant: read it again now and then.
    this.timer = setInterval(() => this.load(), 60_000);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  get closed(): boolean {
    return this.status?.open === false;
  }

  /** "19:00" today, else "lun. 2 nov. 19:00", in the store's time zone. */
  get reopensAtText(): string {
    const at = this.status?.reopensAt ? Date.parse(this.status.reopensAt) : NaN;
    if (Number.isNaN(at)) return "";
    const lang = this.translateService.currentLang || "fr";
    const locale = lang === "ar" ? "ar-MA" : lang === "en" ? "en-GB" : "fr-FR";
    const timeZone = this.status?.timeZoneId || "Africa/Casablanca";
    try {
      const day = (value: number) => new Intl.DateTimeFormat("en-CA", { timeZone }).format(value);
      const sameDay = day(at) === day(Date.now());
      return new Intl.DateTimeFormat(locale, {
        timeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        ...(sameDay ? {} : { weekday: "short", day: "numeric", month: "short" }),
      }).format(at);
    } catch {
      return new Date(at).toLocaleString();
    }
  }

  toggle(): void {
    this.open = !this.open;
    this.error = false;
    this.noNumber = false;
    if (this.open) this.load();
  }

  closeFor(minutes: number): void {
    this.update({ closed: true, closedUntil: new Date(Date.now() + minutes * 60_000).toISOString() });
  }

  closeUntilNextOpening(): void {
    this.update({ closed: true, untilNextOpening: true });
  }

  closeUntilReopened(): void {
    this.update({ closed: true });
  }

  reopen(): void {
    this.update({ closed: false });
  }

  setFollowHours(followHours: boolean): void {
    this.update({ followHours });
  }

  /**
   * The work hours, from the panel: the same drawer as Settings > Company, on the company's first number
   * (the one the store follows when only one has hours). The status is read again when it closes.
   */
  openWorkHours(): void {
    if (this.openingHours) return;
    this.openingHours = true;
    this.noNumber = false;
    this.companyApiService
      .companyNumbersList({ "x-api-canal": "web", "x-api-requestId": uuidv4() }, this.silent)
      .subscribe({
        next: (res) => {
          this.openingHours = false;
          const phone = (res.body || []).find((p) => !!p.reference);
          if (!phone?.reference) {
            this.noNumber = true;
            return;
          }
          this.open = false;
          this.dialog
            .open(WorkHourPopupComponent, {
              width: "min(560px, 100vw)",
              maxWidth: "100vw",
              height: "100vh",
              maxHeight: "100vh",
              position: getCornerDrawerDialogPosition(),
              panelClass: "payment-drawer",
              hasBackdrop: true,
              autoFocus: false,
              enterAnimationDuration: "0ms",
              exitAnimationDuration: "0ms",
              data: { refPhoneCompany: phone.reference },
            })
            .afterClosed()
            .subscribe(() => this.load());
        },
        error: () => {
          this.openingHours = false;
          this.error = true;
        },
      });
  }

  private load(): void {
    this.companyApiService
      .getStoreStatus({ "x-api-canal": "web", "x-api-requestId": uuidv4() }, this.silent)
      .subscribe({
        next: (res) => (this.status = res.body ?? null),
        error: () => (this.status = null),
      });
  }

  private update(body: StoreStatusUpdate): void {
    if (this.saving) return;
    this.saving = true;
    this.error = false;
    this.companyApiService
      .updateStoreStatus({ "x-api-canal": "web", "x-api-requestId": uuidv4(), body }, this.silent)
      .subscribe({
        next: (res) => {
          this.saving = false;
          this.status = res.body ?? this.status;
        },
        error: () => {
          this.saving = false;
          this.error = true;
        },
      });
  }

  @HostListener("document:click", ["$event"])
  onDocumentClick(event: MouseEvent): void {
    if (this.open && !this.hostRef.nativeElement.contains(event.target as Node)) {
      this.open = false;
    }
  }
}
